import { Link, useNavigate } from "react-router-dom";
import { MapPin, Star, ArrowRight, Building2, TreePalm } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { complexUrl, tr } from "@/lib/translations";
import { SiteImg } from "@/components/SiteImg";

export const complexKind = (c) => (c.kind === "finca" ? "finca" : "complex");

export const ComplexCard = ({ complex: c, showIsland = true }) => {
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const kind = complexKind(c);
  const Icon = kind === "finca" ? TreePalm : Building2;
  const url = complexUrl(c);
  const title = tr(c.h1, lang) || tr(c.name, lang);
  const islandName = t.islands[c.island] || c.island;
  const open = (e) => { if (e.target.closest("a,button")) return; navigate(url); };
  const units = c.unit_count === 1 ? t.complex.unitShort : t.complex.unitsShort.replace("{n}", c.unit_count);

  return (
    <article onClick={open} className="group cursor-pointer bg-white rounded-2xl overflow-hidden border border-stone-200/60 card-hover flex flex-col" data-testid={`complex-card-${c.slug}`}>
      <div className="relative aspect-[4/3] overflow-hidden img-hover-zoom bg-tint">
        {c.hero_image && <SiteImg src={c.hero_image} alt={title} loading="lazy" decoding="async" className="w-full h-full object-cover" />}
        <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-white/90 text-stone-800 text-[11px] uppercase tracking-wider font-semibold px-3 py-1" data-testid={`complex-card-kind-${c.slug}`}>
          <Icon className="w-3.5 h-3.5 text-brand" />{t.complex.kindName[kind]}
        </span>
        {c.rating_avg && (
          <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-full bg-white/90 text-stone-900 text-xs font-semibold px-2.5 py-1">
            <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />{c.rating_avg}<span className="text-stone-500 font-normal">({c.reviews_count})</span>
          </span>
        )}
      </div>
      <div className="p-5 flex flex-col gap-2 flex-1">
        <h3 className="font-serif text-lg leading-snug text-stone-900">
          <Link to={url} className="hover:text-brand transition-colors" data-testid={`complex-card-title-${c.slug}`}>{title}</Link>
        </h3>
        <p className="text-sm text-stone-600 flex items-center gap-1.5"><MapPin className="w-4 h-4 text-brand shrink-0" />{[c.location, showIsland ? islandName : null].filter(Boolean).join(", ")}</p>
        <div className="mt-auto pt-3 flex items-center justify-between gap-3 text-sm">
          <span className="text-stone-700 font-medium" data-testid={`complex-card-units-${c.slug}`}>{units}{c.price_from > 0 && <span className="text-stone-500 font-normal"> · {t.complex.priceFromNight.replace("{p}", c.price_from)}</span>}</span>
          <Link to={url} className="inline-flex items-center gap-1 text-brand font-semibold whitespace-nowrap group-hover:gap-2 transition-all">{t.complex.toKind[kind]}<ArrowRight className="w-4 h-4" /></Link>
        </div>
      </div>
    </article>
  );
};
